import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Search, Edit, Trash2, Users, Loader2 } from 'lucide-react';
import { NewEventModal } from '@/components/Events/NewEventModal';
import { eventService } from '@/services/eventService';
import { Event } from '@/types';
import { useToast } from '@/hooks/use-toast';

export const EventsList: React.FC = () => {
  const [showNewEventModal, setShowNewEventModal] = useState(false);
  const [showEditEventModal, setShowEditEventModal] = useState(false);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentEvent, setCurrentEvent] = useState<Event | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Load events on component mount
  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const data = await eventService.getEvents();
      setEvents(data);
    } catch (error) {
      console.error('Error loading events:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los eventos',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (eventData: Omit<Event, 'id' | 'created_at' | 'workspace_id'>) => {
    try {
      const newEvent = await eventService.createEvent(eventData);
      setEvents(prev => [...prev, newEvent]);
      setShowNewEventModal(false);
      toast({
        title: 'Éxito',
        description: 'Evento creado correctamente'
      });
    } catch (error) {
      console.error('Error creating event:', error);
      toast({
        title: 'Error',
        description: 'No se pudo crear el evento',
        variant: 'destructive'
      });
    }
  };

  const handleEditEvent = (event: Event) => {
    setCurrentEvent(event);
    setShowEditEventModal(true);
  };

  const handleUpdateEvent = async (eventData: Omit<Event, 'id' | 'created_at' | 'workspace_id'>) => {
    if (!currentEvent) return;
    
    try {
      const updatedEvent = await eventService.updateEvent(currentEvent.id, eventData);
      setEvents(prev => prev.map(e => e.id === updatedEvent.id ? updatedEvent : e));
      setShowEditEventModal(false);
      toast({
        title: 'Éxito',
        description: 'Evento actualizado correctamente'
      });
    } catch (error) {
      console.error('Error updating event:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el evento',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!confirm('¿Estás seguro de que deseas eliminar este evento?')) {
      return;
    }

    try {
      await eventService.deleteEvent(eventId);
      setEvents(prev => prev.filter(e => e.id !== eventId));
      toast({
        title: 'Éxito',
        description: 'Evento eliminado correctamente'
      });
    } catch (error) {
      console.error('Error deleting event:', error);
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el evento',
        variant: 'destructive'
      });
    }
  };

  const navigateToEventDetails = (eventId: string) => {
    navigate(`/app/events/${eventId}/guests`);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Activo</Badge>;
      case 'DRAFT':
        return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Borrador</Badge>;
      case 'ARCHIVED':
        return <Badge className="bg-gray-100 text-gray-800 border-gray-200">Archivado</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Filter events based on search term
  const filteredEvents = events.filter(event =>
    event.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.contacto1_nombre.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Eventos</h1>
        <Button onClick={() => setShowNewEventModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Evento
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Lista de Eventos</CardTitle>
            <div className="flex items-center space-x-2">
              <Search className="w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar eventos..."
                className="w-64"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredEvents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {events.length === 0 ? 'No hay eventos creados' : 'No se encontraron eventos con ese criterio'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre del Evento</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Contacto Principal</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEvents.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell 
                      className="font-medium cursor-pointer hover:text-blue-600 hover:underline"
                      onClick={() => navigateToEventDetails(event.id)}
                    >
                      {event.nombre}
                    </TableCell>
                    <TableCell>{formatDate(event.fecha)}</TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{event.contacto1_nombre}</div>
                        <div className="text-sm text-gray-500">{event.contacto1_tel}</div>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(event.status)}</TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/app/events/${event.id}/guests`)}
                        >
                          <Users className="w-4 h-4 mr-1" />
                          Gestionar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditEvent(event)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteEvent(event.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Event Modal */}
      <NewEventModal
        open={showNewEventModal}
        onOpenChange={setShowNewEventModal}
        onSubmit={handleCreateEvent}
      />

      {/* Edit Event Modal */}
      <NewEventModal
        open={showEditEventModal}
        onOpenChange={setShowEditEventModal}
        onSubmit={handleUpdateEvent}
        editEvent={currentEvent}
        isEditing={true}
      />
    </div>
  );
};
